/**
 * T2AUniversalDialog.jsx — v4
 * ============================
 * Full-screen dialog styled identically to InternalTransfer:
 *   · Globe fixed behind entire page (same size/opacity/mask)
 *   · Page-level header (like InternalTransfer's h5 + subtitle + icon)
 *   · Left panel: live summary cards + step progress, globe bleeding through
 *   · Right panel: frosted card with 4px accent bar + step-dot sub-header
 *     then the step form inside with generous padding
 *   · Monospace amounts throughout
 *   · Mobile: bottom-sheet above AppShell
 */

import React, { useState, useEffect, useRef } from "react";
import Globe from "../../Pages/Dashboard/dashboard/globe";
import {
  Dialog, Box, Typography, TextField, Button,
  Stack, Chip, Avatar, CircularProgress, Divider, IconButton,
  Paper, Fade, Alert, useMediaQuery,
} from "@mui/material";
import {
  Close         as CloseIcon,
  CardGiftcard  as GiftIcon,
  RequestQuote  as RequestIcon,
  CreditCard    as CardIcon,
  Payment       as PayPalIcon,
  ArrowForward  as ArrowForwardIcon,
  ArrowBack     as ArrowBackIcon,
  CheckCircle   as CheckCircleIcon,
  Send          as SendIcon,
  SwapHoriz     as SwapIcon,
} from "@mui/icons-material";
import { alpha, useTheme, keyframes } from "@mui/material/styles";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import client from "../../API";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import { computeT2A } from "../../Pages/BlitzPay/t2a/T2a.utils";

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const successPop = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  65%  { transform: scale(1.1); }
  100% { transform: scale(1);   opacity: 1; }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(139,92,246,0.4); }
  50%       { box-shadow: 0 0 0 14px rgba(139,92,246,0); }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const APPSHELL_HEIGHT = 86;

const RAILS = [
  { id: "stripe", label: "Card / Bank", sub: "3.5% + $0.30", color: "#635bff", icon: <CardIcon /> },
  { id: "paypal", label: "PayPal",      sub: "2.9% + $0.30", color: "#0070ba", icon: <PayPalIcon /> },
];

const GIFT_PRESETS    = [5, 10, 25, 50, 100, 250];
const REQUEST_PRESETS = [10, 25, 50, 100, 250, 500];
const STEP = { AMOUNT: 0, NOTE: 1, PAY: 2, DONE: 3 };

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const MODE_CFG = {
  gift: {
    verb:        "Send Money",
    icon:        GiftIcon,
    accent:      "#8b5cf6",
    gradient:    "linear-gradient(135deg, #4f46e5 0%, #7c3aed 60%, #a78bfa 100%)",
    presets:     GIFT_PRESETS,
    steps:       ["Amount", "Message", "Payment"],
    successLine: (n, a) => `${fmt(a)} sent to ${n}`,
    successSub:  (n)    => `${n}'s Blitz balance was credited instantly.`,
  },
  request: {
    verb:        "Request Money",
    icon:        RequestIcon,
    accent:      "#10b981",
    gradient:    "linear-gradient(135deg, #059669 0%, #10b981 60%, #34d399 100%)",
    presets:     REQUEST_PRESETS,
    steps:       ["Amount", "Message"],
    successLine: (n, a) => `Invoice for ${fmt(a)} sent`,
    successSub:  (n)    => `${n} received a payment link via email and Blitz.`,
  },
};

// ─── Globe background — exact InternalTransfer treatment ─────────────────────
function GlobeBackground({ accent, size = 1100 }) {
  return (
    <Box aria-hidden="true" sx={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      pointerEvents: "none", zIndex: 0,
      opacity: 0.07,
      transform: "rotate3d(1, 0.3, 0, 22deg)",
      maskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 80%)",
    }}>
      <Globe size={size} color={accent} backgroundColor="transparent" borderColor="transparent" borderWidth={0} />
    </Box>
  );
}

// Globe inside left panel (smaller, same mask)
function PanelGlobe({ accent }) {
  return (
    <Box aria-hidden="true" sx={{
      pointerEvents: "none", opacity: 0.12,
      position: "absolute", inset: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      transform: "rotate3d(1, 0.3, 0, 22deg)",
      maskImage: "radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
      WebkitMaskImage: "radial-gradient(ellipse 75% 75% at 50% 50%, black 40%, transparent 80%)",
    }}>
      <Globe size={480} color={accent} backgroundColor="transparent" borderColor="transparent" borderWidth={0} />
    </Box>
  );
}

// ─── Step dot tracker — exact InternalTransfer pattern ───────────────────────
function StepDots({ steps, currentIdx, accent }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {steps.map((_, i) => (
        <Box key={i} sx={{
          width:  i === currentIdx ? 20 : 7,
          height: 7,
          borderRadius: 99,
          bgcolor: i <= currentIdx ? accent : alpha(accent, 0.2),
          transition: "all 0.3s ease",
        }} />
      ))}
    </Stack>
  );
}

// ─── Card sub-header (inside the frosted step card) ──────────────────────────
function CardSubHeader({ cfg, step, onBack, onClose, isBusy }) {
  const stepIdx  = Math.min(step, cfg.steps.length - 1);
  const canBack  = step > STEP.AMOUNT && step !== STEP.DONE;
  const stepAccent =
    step === STEP.AMOUNT ? cfg.accent
    : step === STEP.NOTE  ? cfg.accent
    : step === STEP.PAY   ? "#f59e0b"
    : cfg.accent;

  return (
    <>
      {/* 4px top accent bar */}
      <Box sx={{
        height: 4,
        background: `linear-gradient(90deg, ${stepAccent}, ${alpha(stepAccent, 0.4)})`,
        transition: "background 0.4s ease",
      }} />

      <Box sx={{
        px: 3, py: 2,
        borderBottom: "1px solid", borderColor: "grey.100",
        display: "flex", alignItems: "center", gap: 1.5,
        bgcolor: alpha(stepAccent, 0.025),
        transition: "background 0.4s ease",
      }}>
        {canBack && (
          <IconButton size="small" onClick={onBack} disabled={isBusy}
            sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}

        <Box sx={{ flex: 1 }}>
          {step === STEP.DONE ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem" }}>
                Blitz T2
              </Typography>
              <Chip label="Complete" size="small" color="success"
                sx={{ height: 18, fontSize: "0.58rem", fontWeight: 700, "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
          ) : (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem" }}>
                Step {stepIdx + 1} of {cfg.steps.length}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {cfg.steps[stepIdx]}
              </Typography>
            </>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {step !== STEP.DONE && (
            <StepDots steps={cfg.steps} currentIdx={stepIdx} accent={stepAccent} />
          )}
          <IconButton size="small" onClick={onClose} disabled={isBusy}
            sx={{ color: "text.secondary", p: 0.75, "&:hover": { color: "text.primary", bgcolor: alpha(cfg.accent, 0.08) } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LEFT PANEL — live summary
// ─────────────────────────────────────────────────────────────────────────────
function SummaryPanel({ cfg, mode, recipientName, parsedAmount, note, rail, calc, step }) {
  const theme = useTheme();
  const Icon  = cfg.icon;

  return (
    <Box sx={{
      height: "100%", overflowY: "auto",
      display: "flex", flexDirection: "column",
      p: 3.5, gap: 2.5,
      position: "relative", zIndex: 1,
    }}>
      {/* Mode badge — matches InternalTransfer page header style */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: "50%",
          background: cfg.gradient,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 16px ${alpha(cfg.accent, 0.38)}`,
        }}>
          <Icon sx={{ color: "white", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.6rem" }}>
            Blitz T2
          </Typography>
          <Typography variant="body1" fontWeight={800}>{cfg.verb}</Typography>
        </Box>
      </Box>

      {/* Recipient */}
      <Paper elevation={0} sx={{
        p: 2, borderRadius: 3,
        border: `1px solid ${alpha(cfg.accent, 0.18)}`,
        bgcolor: alpha(cfg.accent, 0.03),
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: "0.59rem", display: "block", mb: 1.25 }}>
          Recipient
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%", background: `linear-gradient(135deg, ${cfg.accent}, ${alpha(cfg.accent, 0.5)})` }} />
            <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(cfg.accent, 0.15), color: cfg.accent, fontWeight: 800, position: "relative", border: "2.5px solid white", fontSize: "0.95rem" }}>
              {recipientName?.[0]?.toUpperCase() || "?"}
            </Avatar>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>{recipientName}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
              {mode === "gift" ? "receiving transfer" : "invoice recipient"}
            </Typography>
          </Box>
          <CheckCircleIcon sx={{ fontSize: 15, color: cfg.accent, flexShrink: 0 }} />
        </Box>
      </Paper>

      {/* Amount */}
      <Paper elevation={0} sx={{
        p: 2, borderRadius: 3,
        border: `1px solid ${alpha(cfg.accent, 0.18)}`,
        bgcolor: alpha(cfg.accent, 0.025),
      }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: "0.59rem", display: "block", mb: 0.75 }}>
          Amount
        </Typography>
        <Typography sx={{
          fontWeight: 900,
          fontSize: parsedAmount >= 1 ? "2.4rem" : "1.8rem",
          color: parsedAmount >= 1 ? cfg.accent : alpha(theme.palette.text.primary, 0.18),
          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
          letterSpacing: "-0.03em", lineHeight: 1.1,
          transition: "all 0.25s ease",
        }}>
          {parsedAmount >= 1 ? fmt(parsedAmount) : "—"}
        </Typography>
      </Paper>

      {/* Note */}
      {note?.trim() && (
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3,
          border: `1px solid ${alpha(cfg.accent, 0.15)}`,
          bgcolor: alpha(cfg.accent, 0.02),
          animation: `${fadeIn} 0.25s ease`,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: "0.59rem", display: "block", mb: 0.75 }}>
            Message
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", lineHeight: 1.6 }}>
            "{note.trim()}"
          </Typography>
        </Paper>
      )}

      {/* Fee breakdown */}
      {mode === "gift" && step >= STEP.PAY && parsedAmount >= 1 && (
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3,
          border: `1px solid ${alpha(cfg.accent, 0.2)}`,
          bgcolor: alpha(cfg.accent, 0.025),
          animation: `${fadeIn} 0.25s ease`,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: "0.59rem", display: "block", mb: 1.25 }}>
            Cost breakdown
          </Typography>
          <Stack spacing={0.85}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Gift amount</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>{fmt(parsedAmount)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Processing fee</Typography>
              <Typography variant="body2" fontWeight={700} color="error.main" sx={{ fontFamily: "monospace" }}>+{fmt(calc.fee)}</Typography>
            </Box>
            <Divider sx={{ my: 0.25 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" fontWeight={700}>You pay</Typography>
              <Typography fontWeight={900} sx={{ color: cfg.accent, fontFamily: "monospace", fontSize: "1.05rem" }}>{fmt(calc.senderPays)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption" color="text.secondary">{recipientName} receives</Typography>
              <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontFamily: "monospace" }}>{fmt(parsedAmount)}</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      <Box sx={{ flex: 1 }} />

      {/* Step progress pinned to bottom */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "grey.200" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.59rem", display: "block", mb: 1.25 }}>
          Progress
        </Typography>
        <Stack spacing={0.85}>
          {cfg.steps.map((label, i) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: i < step ? alpha(cfg.accent, 0.15) : i === step ? cfg.accent : alpha(theme.palette.text.disabled, 0.1),
                color: i < step ? cfg.accent : i === step ? "white" : "text.disabled",
                fontSize: "0.68rem", fontWeight: 800,
                transition: "all 0.3s ease",
              }}>
                {i < step ? <CheckCircleIcon sx={{ fontSize: 13 }} /> : i + 1}
              </Box>
              <Typography variant="body2"
                fontWeight={i === step ? 700 : 400}
                color={i === step ? "text.primary" : i < step ? cfg.accent : "text.disabled"}
                sx={{ transition: "all 0.3s ease" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP FORMS
// ─────────────────────────────────────────────────────────────────────────────

function AmountStep({ cfg, recipientName, amount, setAmount, parsedAmount, isValidAmount, goNext }) {
  const theme = useTheme();
  return (
    <Box sx={{ animation: `${fadeUp} 0.22s ease` }}>
      {/* Recipient pill */}
      <Paper variant="outlined" sx={{
        p: 2, borderRadius: 2.5, mb: 3.5,
        display: "flex", alignItems: "center", gap: 1.5,
        borderColor: alpha(cfg.accent, 0.22), bgcolor: alpha(cfg.accent, 0.03),
      }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box sx={{ position: "absolute", inset: -2.5, borderRadius: "50%", background: `linear-gradient(135deg, ${cfg.accent}, ${alpha(cfg.accent, 0.5)})` }} />
          <Avatar sx={{ width: 38, height: 38, bgcolor: alpha(cfg.accent, 0.15), color: cfg.accent, fontWeight: 800, fontSize: "0.9rem", position: "relative", border: "2.5px solid white" }}>
            {recipientName?.[0]?.toUpperCase() || "?"}
          </Avatar>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem" }}>
            {cfg.verb.toLowerCase().includes("send") ? "Sending to" : "Requesting from"}
          </Typography>
          <Typography variant="body2" fontWeight={700}>{recipientName}</Typography>
        </Box>
        <CheckCircleIcon sx={{ fontSize: 14, color: cfg.accent }} />
      </Paper>

      {/* Big amount input */}
      <Paper elevation={0} sx={{
        p: 4, mb: 3.5, borderRadius: 3, textAlign: "center",
        border: `1.5px solid ${isValidAmount ? alpha(cfg.accent, 0.5) : alpha(theme.palette.divider, 0.6)}`,
        bgcolor: alpha(cfg.accent, 0.025),
        transition: "border-color 0.2s",
      }}>
        <Typography variant="caption" sx={{ color: cfg.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.6rem", display: "block", mb: 1.5 }}>
          Amount (USD)
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace", color: "text.disabled", fontWeight: 300, fontSize: "3rem", lineHeight: 1 }}>
            $
          </Typography>
          <TextField variant="standard" type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
            inputProps={{ min: 1, step: 1, style: { textAlign: "center", fontSize: "3rem", fontWeight: 900, width: "160px", letterSpacing: "-0.03em", fontFamily: "'JetBrains Mono', 'Fira Code', monospace" } }}
            InputProps={{ disableUnderline: true }}
            sx={{ "& input": { color: isValidAmount ? cfg.accent : "text.primary" } }}
            onKeyDown={(e) => e.key === "Enter" && goNext()}
          />
        </Box>
        {!isValidAmount && amount && (
          <Typography variant="caption" color="error.main" sx={{ mt: 0.75, display: "block", fontWeight: 600 }}>Minimum $1.00</Typography>
        )}
      </Paper>

      {/* Presets */}
      <Box sx={{ mb: 3.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.6rem", display: "block", mb: 1.25 }}>
          Quick select
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {cfg.presets.map((p) => (
            <Chip key={p} label={`$${p}`} size="small"
              onClick={() => setAmount(String(p))}
              variant={String(p) === amount ? "filled" : "outlined"}
              sx={{
                fontWeight: 700, fontSize: "0.85rem", height: 38, px: 0.5, cursor: "pointer",
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                borderColor: alpha(cfg.accent, 0.35),
                ...(String(p) === amount
                  ? { bgcolor: cfg.accent, color: "white", "& .MuiChip-label": { color: "white" } }
                  : { color: cfg.accent }),
                transition: "all 0.15s ease",
                "&:hover": { borderColor: cfg.accent, bgcolor: alpha(cfg.accent, 0.08) },
              }} />
          ))}
        </Stack>
      </Box>

      <Button variant="contained" fullWidth size="large" onClick={goNext} disabled={!isValidAmount}
        endIcon={<ArrowForwardIcon />}
        sx={{
          height: 54, borderRadius: 2.5, fontWeight: 700, fontSize: "0.97rem",
          background: isValidAmount ? cfg.gradient : undefined,
          boxShadow: isValidAmount ? `0 6px 22px ${alpha(cfg.accent, 0.38)}` : "none",
          "&:hover": { transform: "translateY(-1px)" }, transition: "all 0.2s ease",
        }}>
        Continue
      </Button>
    </Box>
  );
}

function NoteStep({ cfg, mode, recipientName, parsedAmount, note, setNote, noteValid, goNext, goBack, isBusy }) {
  return (
    <Box sx={{ animation: `${fadeUp} 0.22s ease` }}>
      <Paper variant="outlined" sx={{
        p: 2, borderRadius: 2.5, mb: 3.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderColor: alpha(cfg.accent, 0.22), bgcolor: alpha(cfg.accent, 0.03),
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: alpha(cfg.accent, 0.15), color: cfg.accent, fontWeight: 800, width: 38, height: 38, fontSize: "0.9rem" }}>
            {recipientName?.[0]?.toUpperCase() || "?"}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={700}>{recipientName}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              {mode === "gift" ? "receiving" : "invoiced"}
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontWeight: 900, color: cfg.accent, fontSize: "1.5rem", fontFamily: "'JetBrains Mono', 'Fira Code', monospace", letterSpacing: "-0.02em" }}>
          {fmt(parsedAmount)}
        </Typography>
      </Paper>

      <Box sx={{ p: 2.5, borderRadius: 2.5, mb: 3.5, border: "1px solid", borderColor: alpha(cfg.accent, 0.18), bgcolor: alpha(cfg.accent, 0.02) }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <Typography variant="body2" fontWeight={700}>
            {mode === "gift" ? "What's this for?" : "Request description"}
          </Typography>
          <Chip label="Required" size="small" sx={{ height: 17, fontSize: "0.58rem", fontWeight: 700, ml: "auto", bgcolor: alpha(cfg.accent, 0.1), color: cfg.accent, "& .MuiChip-label": { px: 0.75 } }} />
        </Box>
        <TextField fullWidth multiline minRows={4} maxRows={7}
          autoFocus value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={mode === "gift" ? "e.g. Great work on the collab, congrats on the launch! 🎉" : "e.g. Content fee — February campaign, Invoice #042"}
          error={note.length > 0 && !noteValid}
          helperText={note.length > 0 && !noteValid ? "At least 3 characters" : `${note.length} / 280`}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 }, "& .MuiFormHelperText-root": { textAlign: note.length > 0 && !noteValid ? "left" : "right" } }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && noteValid) { e.preventDefault(); goNext(); } }}
        />
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" onClick={goBack} startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
          sx={{ borderRadius: 2.5, minWidth: 100, fontWeight: 700, height: 54 }}>
          Back
        </Button>
        <Button variant="contained" fullWidth size="large" onClick={goNext}
          disabled={!noteValid || isBusy}
          endIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : mode === "gift" ? <ArrowForwardIcon /> : <SendIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 54, borderRadius: 2.5, fontWeight: 700, fontSize: "0.97rem",
            background: cfg.gradient,
            boxShadow: `0 6px 22px ${alpha(cfg.accent, 0.35)}`,
            "&:hover": { transform: "translateY(-1px)" }, transition: "all 0.2s ease",
          }}>
          {mode === "gift" ? "Choose payment →" : isBusy ? "Sending…" : "Send request"}
        </Button>
      </Stack>
    </Box>
  );
}

function PayStep({ cfg, recipientName, parsedAmount, rail, setRail, calc, goNext, goBack, isBusy }) {
  const theme = useTheme();
  return (
    <Box sx={{ animation: `${fadeUp} 0.22s ease` }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.09em", fontSize: "0.62rem", display: "block", mb: 1.25 }}>
        Payment method
      </Typography>

      <Stack spacing={1.25} sx={{ mb: 3.5 }}>
        {RAILS.map((r) => {
          const active = rail === r.id;
          return (
            <Paper key={r.id} onClick={() => setRail(r.id)} variant="outlined"
              sx={{
                p: 2.5, borderRadius: 2.5, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 2,
                borderColor: active ? r.color : alpha(theme.palette.divider, 0.8),
                borderWidth: active ? 2 : 1,
                bgcolor: active ? alpha(r.color, 0.05) : "transparent",
                transition: "all 0.15s ease", position: "relative", overflow: "hidden",
                "&:hover": { borderColor: r.color, boxShadow: `0 3px 14px ${alpha(r.color, 0.14)}` },
                "&::before": active ? { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, background: r.color } : {},
              }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: alpha(r.color, active ? 0.14 : 0.07), display: "flex", alignItems: "center", justifyContent: "center", color: r.color, flexShrink: 0 }}>
                {React.cloneElement(r.icon, { sx: { fontSize: 22 } })}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={700}>{r.label}</Typography>
                <Typography variant="caption" color="text.secondary">{r.sub} fee</Typography>
              </Box>
              {active && <CheckCircleIcon sx={{ color: r.color, fontSize: 20 }} />}
            </Paper>
          );
        })}
      </Stack>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, mb: 3.5, border: "1px solid", borderColor: alpha(cfg.accent, 0.18), bgcolor: alpha(cfg.accent, 0.02) }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem", display: "block", mb: 1.5 }}>
          Cost breakdown
        </Typography>
        <Stack spacing={0.9}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">Gift amount</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>{fmt(parsedAmount)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">Processing fee</Typography>
            <Typography variant="body2" fontWeight={700} color="error.main" sx={{ fontFamily: "monospace" }}>+{fmt(calc.fee)}</Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="body2" fontWeight={700}>You pay</Typography>
            <Typography fontWeight={900} sx={{ color: cfg.accent, fontFamily: "monospace", fontSize: "1.1rem" }}>{fmt(calc.senderPays)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">{recipientName} receives</Typography>
            <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontFamily: "monospace" }}>{fmt(parsedAmount)}</Typography>
          </Box>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ mb: 3.5, borderRadius: 2 }}>
        <Typography variant="caption" fontWeight={600}>
          {recipientName}'s Blitz balance is credited immediately after payment.
        </Typography>
      </Alert>

      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" onClick={goBack} startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
          sx={{ borderRadius: 2.5, minWidth: 100, fontWeight: 700, height: 54 }}>
          Back
        </Button>
        <Button variant="contained" fullWidth size="large" onClick={goNext} disabled={isBusy}
          endIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 54, borderRadius: 2.5, fontWeight: 700, fontSize: "0.97rem",
            background: cfg.gradient,
            animation: !isBusy ? `${pulseGlow} 2.5s infinite` : "none",
            boxShadow: `0 6px 22px ${alpha(cfg.accent, 0.4)}`,
            "&:hover": { transform: "translateY(-1px)" }, transition: "all 0.2s ease",
          }}>
          {isBusy ? "Redirecting…" : `Pay ${fmt(calc.senderPays)}`}
        </Button>
      </Stack>
    </Box>
  );
}

function DoneStep({ cfg, mode, recipientName, parsedAmount, onClose }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, animation: `${fadeUp} 0.35s ease` }}>
      <Box sx={{
        width: 90, height: 90, borderRadius: "50%",
        background: cfg.gradient,
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: 3.5,
        boxShadow: `0 20px 50px ${alpha(cfg.accent, 0.45)}`,
        animation: `${successPop} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
      }}>
        <CheckCircleIcon sx={{ color: "white", fontSize: 50 }} />
      </Box>
      <Typography variant="h4" fontWeight={900} sx={{
        mb: 1, textAlign: "center", letterSpacing: "-0.02em",
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      }}>
        {cfg.successLine(recipientName, parsedAmount)}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", mb: 5, maxWidth: 320, lineHeight: 1.65 }}>
        {cfg.successSub(recipientName)}
      </Typography>
      <Button variant="contained" onClick={onClose}
        sx={{
          height: 52, borderRadius: 2.5, fontWeight: 700, px: 6,
          background: cfg.gradient,
          boxShadow: `0 8px 28px ${alpha(cfg.accent, 0.4)}`,
          "&:hover": { transform: "translateY(-1px)" }, transition: "all 0.2s ease",
        }}>
        Done
      </Button>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────
export default function T2AUniversalDialog({
  open,
  onClose,
  mode = "gift",
  recipientName = "them",
  recipientGlobalUserId = null,
  resolveRecipientGUId = null,
}) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const queryClient = useQueryClient();
  const cfg = MODE_CFG[mode] || MODE_CFG.gift;

  const { isAuthorized: userOK }                  = useAuth();
  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();

  const [step,       setStep]       = useState(STEP.AMOUNT);
  const [amount,     setAmount]     = useState("");
  const [note,       setNote]       = useState("");
  const [rail,       setRail]       = useState("stripe");
  const [resolving,  setResolving]  = useState(false);
  const [resolvedId, setResolvedId] = useState(recipientGlobalUserId);
  const [senderId,   setSenderId]   = useState(null);
  const hasFired = useRef(false);

  useEffect(() => {
    if (open) {
      setStep(STEP.AMOUNT); setAmount(""); setNote(""); setRail("stripe");
      setResolvedId(recipientGlobalUserId); hasFired.current = false;
    }
  }, [open, mode, recipientGlobalUserId]);

  useEffect(() => {
    if (!open || resolvedId || hasFired.current) return;
    if (resolveRecipientGUId) {
      hasFired.current = true; setResolving(true);
      resolveRecipientGUId()
        .then((id) => setResolvedId(id))
        .catch(() => toast.error("Couldn't resolve recipient — try again"))
        .finally(() => setResolving(false));
    }
  }, [open, resolvedId, resolveRecipientGUId]);

  useEffect(() => {
    if (!open || senderId) return;
    client.balanceLedger.fetchMyBalance()
      .then((d) => setSenderId(d?.user?.id ?? null))
      .catch(() => {});
  }, [open]);

  const parsedAmount  = parseFloat(amount) || 0;
  const isValidAmount = parsedAmount >= 1;
  const calc          = computeT2A(parsedAmount, rail, "sender");
  const noteValid     = note.trim().length >= 3;

  const giftMutation = useMutation(
    (payload) => client.balanceLedger.t2aGift(payload),
    {
      onSuccess: (data) => {
        const url = data?.approval_url || data?.url;
        if (url) { toast.info("Redirecting to payment…"); setTimeout(() => { window.location.href = url; }, 400); }
        else setStep(STEP.DONE);
      },
      onError: (err) => toast.error("Gift failed", { description: err?.response?.data?.error || err?.message }),
    }
  );

  const requestMutation = useMutation(
    (payload) => client.balanceLedger.requestTransfer(payload),
    {
      onSuccess: () => { setStep(STEP.DONE); queryClient.invalidateQueries(["balanceHistory"]); },
      onError: (err) => toast.error("Request failed", { description: err?.response?.data?.error || err?.message }),
    }
  );

  const busy = giftMutation.isLoading || requestMutation.isLoading;

  const handleGiftConfirm = () => {
    if (!senderId)   { toast.error("Balance not loaded — refresh"); return; }
    if (!resolvedId) { toast.error("Recipient not resolved — try again"); return; }
    giftMutation.mutate({
      sender_id: senderId, description: note.trim() || "Gift from profile",
      payment_rail: rail, fee_payer: "sender",
      transfers: [{ type: "internal", amount: String(calc.senderPays), recipient_global_user_id: resolvedId,
        metadata: { t2a: true, agreed_amount: parsedAmount, fee_amount: parseFloat((calc.senderPays - parsedAmount).toFixed(2)), fee_payer: "sender", payment_rail: rail } }],
    });
  };

  const handleRequestConfirm = () => {
    if (!senderId)   { toast.error("Balance not loaded — refresh"); return; }
    if (!resolvedId) { toast.error("Recipient not resolved — try again"); return; }
    requestMutation.mutate({
      requester_id: senderId, description: note.trim() || "Payment request",
      invoices: [{ type: "internal", amount: String(parsedAmount), label: recipientName, recipient_global_user_id: resolvedId }],
    });
  };

  const goNext = () => {
    if (step === STEP.AMOUNT) { if (!isValidAmount) { toast.error("Minimum $1.00"); return; } setStep(STEP.NOTE); return; }
    if (step === STEP.NOTE)   { if (!noteValid) { toast.error("Add a short message (3+ chars)"); return; } if (mode === "gift") { setStep(STEP.PAY); return; } handleRequestConfirm(); return; }
    if (step === STEP.PAY)    { handleGiftConfirm(); return; }
  };
  const goBack = () => {
    if (step === STEP.NOTE) setStep(STEP.AMOUNT);
    if (step === STEP.PAY)  setStep(STEP.NOTE);
  };

  const renderStep = () => {
    if (resolving) return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
        <CircularProgress size={40} sx={{ color: cfg.accent }} />
        <Typography variant="body2" color="text.secondary">Verifying recipient…</Typography>
      </Box>
    );
    if (step === STEP.DONE)   return <DoneStep   cfg={cfg} mode={mode} recipientName={recipientName} parsedAmount={parsedAmount} onClose={onClose} />;
    if (step === STEP.AMOUNT) return <AmountStep cfg={cfg} recipientName={recipientName} amount={amount} setAmount={setAmount} parsedAmount={parsedAmount} isValidAmount={isValidAmount} goNext={goNext} />;
    if (step === STEP.NOTE)   return <NoteStep   cfg={cfg} mode={mode} recipientName={recipientName} parsedAmount={parsedAmount} note={note} setNote={setNote} noteValid={noteValid} goNext={goNext} goBack={goBack} isBusy={busy} />;
    if (step === STEP.PAY)    return <PayStep    cfg={cfg} recipientName={recipientName} parsedAmount={parsedAmount} rail={rail} setRail={setRail} calc={calc} goNext={goNext} goBack={goBack} isBusy={busy} />;
    return null;
  };

  // ─── Mobile ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Dialog open={open} onClose={!busy ? onClose : undefined} maxWidth="sm" fullWidth
        PaperProps={{ sx: {
          borderRadius: "24px 24px 0 0",
          maxHeight: `calc(100vh - ${APPSHELL_HEIGHT}px)`,
          m: 0, width: "100%", maxWidth: "100%",
          position: "fixed", bottom: `${APPSHELL_HEIGHT}px`, left: 0, right: 0,
          overflow: "hidden",
          bgcolor: theme.palette.mode === "dark" ? "#09090f" : "#f4f5f8",
        }}}
        sx={{ zIndex: theme.zIndex.modal + 1, "& .MuiDialog-container": { alignItems: "flex-end" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: alpha(theme.palette.text.primary, 0.12) }} />
        </Box>
        <CardSubHeader cfg={cfg} step={step} onBack={goBack} onClose={onClose} isBusy={busy} />
        <Box sx={{ overflowY: "auto", p: 3 }}>
          <Fade in key={step}><Box>{renderStep()}</Box></Fade>
        </Box>
      </Dialog>
    );
  }

  // ─── Desktop: fullScreen — same shell structure as InternalTransfer ────────
  return (
    <Dialog open={open} onClose={!busy ? onClose : undefined} fullScreen
      PaperProps={{ sx: { m: 0, borderRadius: 0, bgcolor: theme.palette.mode === "dark" ? "#09090f" : "#f2f3f7", overflow: "hidden" } }}
      sx={{ zIndex: theme.zIndex.modal + 1 }}
    >
      {/* Globe fixed behind entire page — same as InternalTransfer */}
      <GlobeBackground accent={cfg.accent} />

      {/* Page header — mirrors InternalTransfer's h5 + subtitle + icon */}
      <Box sx={{
        position: "relative", zIndex: 10,
        px: { md: 4, lg: 5 }, py: 2.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.75),
        backdropFilter: "blur(12px)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: "50%",
            background: cfg.gradient,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 16px ${alpha(cfg.accent, 0.35)}`,
            transition: "all 0.3s ease",
          }}>
            {mode === "gift"
              ? <GiftIcon sx={{ fontSize: 24, color: "white" }} />
              : <RequestIcon sx={{ fontSize: 24, color: "white" }} />}
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.02em", mb: 0.1 }}>
              {cfg.verb}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {mode === "gift" ? `Send money to ${recipientName}.` : `Request payment from ${recipientName}.`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={busy}
          sx={{ bgcolor: alpha(theme.palette.text.primary, 0.06), "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.12) } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Two-column body */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", position: "relative", zIndex: 1 }}>

        {/* LEFT: summary panel with its own globe */}
        <Box sx={{
          width: 360, flexShrink: 0,
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.75),
          backdropFilter: "blur(8px)",
          position: "relative", overflow: "hidden",
        }}>
          <PanelGlobe accent={cfg.accent} />
          <SummaryPanel cfg={cfg} mode={mode} recipientName={recipientName}
            parsedAmount={parsedAmount} note={note} rail={rail} calc={calc} step={step} />
        </Box>

        {/* RIGHT: step form in frosted card */}
        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          <Box sx={{ maxWidth: 620, width: "100%", mx: "auto", px: { md: 4, lg: 7 }, py: 4, flex: 1 }}>

            {/* The frosted card — identical to InternalTransfer's main Paper */}
            <Paper elevation={0} sx={{
              border: "1px solid", borderColor: "grey.200",
              borderRadius: 3.5, overflow: "hidden",
              backdropFilter: "blur(2px)",
              bgcolor: alpha(theme.palette.background.paper, 0.92),
            }}>
              <CardSubHeader cfg={cfg} step={step} onBack={goBack} onClose={onClose} isBusy={busy} />
              <Box sx={{ p: { md: 4, lg: 5 } }}>
                <Fade in key={step}><Box>{renderStep()}</Box></Fade>
              </Box>
            </Paper>

          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}